import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import { CircularProgress, TableContainer, TablePagination, TextField } from "@mui/material";
import { RestartAlt } from "@mui/icons-material";
import { getCategories, deleteCategory, updateCategory } from ".";
import { StyledTableCell, StyledTableRow } from '../../utils/constantData'
import { resolveMediaUrl } from "../../utils/mediaUrl";
import { AdminPageLayout, AdminSectionCard, TableRowActions, DeleteConfirmationDialog, AdminAction } from "../../components/admin";
import { adminResetButtonSx } from "../../components/admin/adminButtonStyles";

export default function Category() {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [video, setVideo] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);
  const [searchInput, setSearchInput] = React.useState("");
  const [filterKey, setFilterKey] = React.useState(0);

  const hasActiveFilters = !!searchInput.trim();
  const startIndex = page * rowsPerPage;

  const filteredRows = React.useMemo(() => {
    const query = searchInput.trim().toLowerCase();
    if (!query) return video;
    return video.filter((row) => (row.name || "").toLowerCase().includes(query));
  }, [video, searchInput]);

  const navigate = useNavigate();
  const location = useLocation();
  const { results } = location.state || {};
  const { update } = location.state || {};

  React.useEffect(() => {
    if (results) {
      toast.success("Category Added successfully!", {
        position: "bottom-right",
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        toastId: 'success1',
        autoClose: 2000,
        progress: undefined,
        theme: "light",
        hideProgressBar: false,
      });
    } else if (update) {
      toast.success("Category Updated successfully!", {
        toastId: 'success1',
        autoClose: 2000,
        closeOnClick: true,
        position: "bottom-right",
        draggable: true,
        hideProgressBar: false,
        pauseOnHover: true,
        progress: undefined,
        theme: "light",
      });
    }
  }, []);

  const fetchData = async () => {
    setLoading(true)
    try {
      const response = await getCategories();
      const responseData = response.data;
      setLoading(false)
      setVideo(responseData?.data || []);
    } catch (error) {
      setLoading(false);
      toast.error(error.response?.data?.message || "Failed to load categories", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        theme: "light",
      });
      console.log(error);
    }
  };

  React.useEffect(() => {
    fetchData();
  }, []);

  React.useEffect(() => {
    setPage(0);
  }, [searchInput]);

  const handleResetFilters = () => {
    setSearchInput("");
    setPage(0);
    setFilterKey((key) => key + 1);
  };

  const navigateToAddCategory = () => {
    navigate(`/category/add`);
  };

  const handleEdit = React.useCallback(async (id) => {
    navigate(`/category/${id}`)
  }, [])

  const handleDelete = React.useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const result = await deleteCategory(deleteTarget.id);
      if (result) {
        fetchData();
        toast.success("Category Deleted Successfully!", {
          toastId: 'success1',
          position: "bottom-right",
          autoClose: 1000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
        setDeleteTarget(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete category", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        theme: "light",
      });
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget])

  const handleStatus = async (nextValue, id) => {
    try {
      const formData = new FormData();
      formData.append("active", nextValue)
      const response = await updateCategory(id, formData);
      if (response) {
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update category status", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        theme: "light",
      });
      console.log(error);
    }
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  return (
    <AdminPageLayout
      eyebrow="Content Structure"
      title="Categories"
      description="Organize top-level content groups and control their status without changing any backend category logic."
      actions={
        <div className="admin-toolbar" style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <TextField
            key={`search-${filterKey}`}
            id="category-search"
            label="Search categories"
            value={searchInput}
            size="small"
            sx={{ minWidth: 220 }}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setPage(0);
            }}
          />
          <AdminAction
            id="reset-filters-btn"
            variant="outline"
            startIcon={<RestartAlt />}
            disabled={!hasActiveFilters}
            onClick={handleResetFilters}
            sx={{ whiteSpace: "nowrap", ...adminResetButtonSx(hasActiveFilters) }}
          >
            Reset
          </AdminAction>
          <AdminAction onClick={navigateToAddCategory}>Add Category</AdminAction>
        </div>
      }
    >
      <AdminSectionCard
        title="Category list"
        description="Manage category records, toggle status, and open edits from a cleaner control surface."
        className="admin-table-shell"
      >
        <TableContainer component="div">
          <Table sx={{ minWidth: 700 }} aria-label="categories table">
            <TableHead>
              <StyledTableRow>
                <StyledTableCell>Sr. No.</StyledTableCell>
                <StyledTableCell>Image</StyledTableCell>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell>Created By</StyledTableCell>
                <StyledTableCell align="right">Actions</StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {filteredRows.length !== 0 ? (
                filteredRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => (
                    <StyledTableRow key={row.id}>
                      <StyledTableCell component="th">
                        {startIndex + index + 1}
                      </StyledTableCell>
                      <StyledTableCell>
                        <img
                          src={row.imageUrl ? resolveMediaUrl(row.imageUrl) : "/placeholder-image.jpg"}
                          alt={row.name || "Category"}
                          className="h-12 w-12 rounded object-cover bg-zinc-100"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = "/placeholder-image.jpg";
                          }}
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        <div className="font-semibold text-[15px] text-[#172112]">{row.name}</div>
                      </StyledTableCell>
                      <StyledTableCell>
                        {row.creator?.fullName || "—"}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        <TableRowActions
                          rowId={row.id}
                          onEdit={() => handleEdit(row.id)}
                          onDelete={() => setDeleteTarget(row)}
                          status={{
                            isActive: row.active === 1,
                            onToggle: () => handleStatus(row.active === 1 ? 0 : 1, row.id, row.name),
                          }}
                        />
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
              ) : loading ? (
                <tr>
                  <td colSpan={5} className="text-center py-8">
                    <CircularProgress size={20} className="text-zinc-500" />
                    <span className="ml-2 text-zinc-500">Loading...</span>
                  </td>
                </tr>
              ) : (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-zinc-500">
                    No categories found.
                  </td>
                </tr>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </AdminSectionCard>

      <DeleteConfirmationDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Category"
        itemName={deleteTarget?.name}
        loading={deleting}
      />
    </AdminPageLayout>
  );
}
